import { useRef, useEffect, useState } from "react";
import { styled } from "styled-components";
import type { FormData, ApiPatient, User, ApiMarket } from "core/types";

type WindowWithSendSafelyDropzone = Window & {
  SendSafelyDropzone?: new (
    apiKey: string,
    targetElement: HTMLElement | null,
  ) => SendSafelyDropzoneInstance;
};
declare const window: WindowWithSendSafelyDropzone;

type SendSafelyDropzoneInstance = {
  url: string;
  invokeDropzoneConnectors: boolean;
  disableAutoSubmit: boolean;
  initialize: () => void;
  setUnconfirmedSender: (email: string) => void;
  setPackageLabel: (label: string) => void;
  addMessage: (message: string) => void;
  finalizePackage: (
    success: (result: string) => void,
    error: (err: unknown) => void,
  ) => void;
};

type SubmitResult = { success: boolean; redirect: boolean };

type SubmitArgs = {
  formData: FormData;
  patient: ApiPatient;
  user: User;
  markets: ApiMarket[];
  onResult: (result: SubmitResult) => void;
};

const submitViaSendSafely = (
  sendSafelyDropzone: SendSafelyDropzoneInstance,
  { formData, patient, user, markets, onResult }: SubmitArgs,
) => {
  const codeISO2ByPatientCountryId = markets.find(
    (x) => x?.countryId === patient?.countryId,
  )?.codeISO2;

  if (!codeISO2ByPatientCountryId || patient === null) {
    return;
  }

  const body = `Recipient:
        ${formData.name_clinic}
            ${formData.address}

            Patient info:
            ${patient.firstName} ${patient.lastName}, ${
              patient?.nationalIdentity ?? ""
            }
            Email: ${patient.email}
            Phone number: ${patient.phoneNumber}
            UserId: ${patient.userId}

            Psychologist name:
            ${user.firstName} ${user.lastName}

            ${formData.type}:
            ${formData.referral_certificate}`;

  sendSafelyDropzone.setUnconfirmedSender(user.email ?? "");
  sendSafelyDropzone.setPackageLabel(
    `MC ${formData.type} - market: ${codeISO2ByPatientCountryId}`,
  );
  sendSafelyDropzone.addMessage(body);
  sendSafelyDropzone.finalizePackage(
    () => {
      onResult({ success: true, redirect: true });
    },
    (error) => {
      console.error("Error finalizing", error);
      onResult({ success: false, redirect: false });
    },
  );
};

export const useSendSafelyInit = () => {
  const scriptsInited = useRef(false);
  const sendSafelyRef = useRef<SendSafelyDropzoneInstance>();
  const [isReady, setIsReady] = useState(false);
  const SENDSAFELY_BASE_URL = import.meta.env.VITE_SENDSAFELY_BASE_URL;
  const SENDSAFELY_DROPZONE_KEY = import.meta.env.VITE_SENDSAFELY_DROPZONE_KEY;
  const placeholderId = "SS_Dropzone";
  const jqScriptId = "jq";
  const ssdzScriptId = "ssdz";

  useEffect(() => {
    if (scriptsInited.current) return;

    if (
      document.getElementById(jqScriptId) ||
      document.getElementById(ssdzScriptId)
    ) {
      if (window.SendSafelyDropzone) onLoad();
      scriptsInited.current = true;
      return;
    }
    const jq = document.createElement("script");
    const ssdz = document.createElement("script");
    jq.id = jqScriptId;
    ssdz.id = ssdzScriptId;
    jq.setAttribute("src", "/sendSafely/jquery-3.7.1.min.js");
    ssdz.setAttribute("src", "/sendSafely/SendSafelyDropzone.v2.js");
    ssdz.setAttribute("type", "module");
    ssdz.onload = onLoad;
    document.head.appendChild(jq);
    document.head.appendChild(ssdz);
    scriptsInited.current = true;

    function onLoad() {
      const placeholder = document.getElementById(placeholderId);
      sendSafelyRef.current = new window.SendSafelyDropzone!(
        SENDSAFELY_DROPZONE_KEY,
        placeholder,
      ) as SendSafelyDropzoneInstance;
      sendSafelyRef.current.url = SENDSAFELY_BASE_URL;
      sendSafelyRef.current.invokeDropzoneConnectors = true;
      sendSafelyRef.current.disableAutoSubmit = true;
      sendSafelyRef.current.initialize();
      setIsReady(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const DropzoneComponent = (
    <DropzoneWrapper>
      <div id={placeholderId}></div>
    </DropzoneWrapper>
  );
  const submitHandler = (args: SubmitArgs) => {
    if (!sendSafelyRef.current) return;
    submitViaSendSafely(sendSafelyRef.current, args);
  };

  return {
    submitHandler,
    Component: DropzoneComponent,
    isReady,
  };
};

const DropzoneWrapper = styled.div`
  display: none;
`;
